import React, { useState, useEffect } from "react"
import { Tabs } from "radix-ui"
import { Button } from "src/components/ui/button"
import { TeamBasicsTab } from "./TeamBasicsTab"
import { TeamMembersTab } from "./TeamMembersTab"

const DEFAULT_COLOR = "#64748B"
const HEX_RE = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/

function tabTriggerClass(active) {
  return [
    "px-3 py-1.5 text-[13px] font-medium rounded-md transition-colors",
    active ? "bg-slate-900 text-white shadow-sm" : "text-slate-500 hover:text-slate-700",
  ].join(" ")
}

export function TeamForm({ team, onSubmit, onCancel, isPending }) {
  const isEdit = !!team?.id
  const [tab, setTab] = useState("basics")
  const [form, setForm] = useState({ name: "", description: "", color: DEFAULT_COLOR })
  const [memberIds, setMemberIds] = useState([])
  const [errors, setErrors] = useState({})

  useEffect(() => {
    setTab("basics")
    setErrors({})
    if (team?.id) {
      setForm({
        name: team.name || "",
        description: team.description || "",
        color: team.color || DEFAULT_COLOR,
      })
      setMemberIds((team.members || []).map((m) => m.membership_id))
    } else {
      setForm({ name: "", description: "", color: DEFAULT_COLOR })
      setMemberIds([])
    }
  }, [team])

  const set = (field, value) => setForm((f) => ({ ...f, [field]: value }))

  function validate() {
    const e = {}
    if (!form.name.trim()) e.name = "Name is required"
    if (!HEX_RE.test(form.color)) e.color = "Must be a valid hex color (e.g. #3b82f6)"
    return e
  }

  const hasMembers = memberIds.length > 0
  const canSubmit = form.name.trim() && hasMembers && !isPending

  function handleSubmit(e) {
    e.preventDefault()
    const e2 = validate()
    if (Object.keys(e2).length) {
      setErrors(e2)
      setTab("basics") // surface the basics errors
      return
    }
    if (!hasMembers) {
      setTab("members") // surface the members requirement
      return
    }
    onSubmit({
      name: form.name.trim(),
      description: form.description.trim() || undefined,
      color: form.color,
      member_ids: memberIds,
    })
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-1 flex-col overflow-hidden">
      <Tabs.Root value={tab} onValueChange={setTab} className="flex flex-1 flex-col overflow-hidden">
        <div className="px-6 pt-4">
          <Tabs.List className="inline-flex gap-1 rounded-lg bg-slate-100 p-1">
            <Tabs.Trigger value="basics" className={tabTriggerClass(tab === "basics")}>
              Basics
            </Tabs.Trigger>
            <Tabs.Trigger value="members" className={tabTriggerClass(tab === "members")}>
              Members{memberIds.length ? ` (${memberIds.length})` : ""}
            </Tabs.Trigger>
          </Tabs.List>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          <Tabs.Content value="basics" className="focus:outline-none">
            <TeamBasicsTab form={form} set={set} errors={errors} />
          </Tabs.Content>
          <Tabs.Content value="members" className="focus:outline-none">
            <TeamMembersTab selectedIds={memberIds} onChange={setMemberIds} />
            {!hasMembers && (
              <p className="mt-3 text-[12px] text-amber-600">Add at least one member to save the team.</p>
            )}
          </Tabs.Content>
        </div>
      </Tabs.Root>

      <div className="flex justify-end gap-3 border-t border-slate-100 px-6 py-4">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isPending}>
          Cancel
        </Button>
        <Button type="submit" disabled={!canSubmit}>
          {isPending ? "Saving…" : isEdit ? "Save changes" : "Create team"}
        </Button>
      </div>
    </form>
  )
}
